import _ = require('underscore');

import { Filter } from '~commonInfra/utils/Filter';
import { Messages } from '~src/codegen/Localize';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { ReadOnlyEditableConnection } from '~src/connectivity/components/EditConnection/utils/EditConnectionActionTypes';
import { EditConnectionReactActionUtils } from '~src/connectivity/components/EditConnection/utils/EditConnectionActionUtils';
import { OAuthCredentials } from '~src/connectivity/resources/OAuthCredentials';
import { IGetCommonUserOAuthCredentialResult } from '~src/connectivity/serverApi/requests/OAuth/GetCommonUserOAuthCredentialRequest';
import { IOAuthCredential,IOAuthCredentialTypeInfo } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { OAuthCredentialsService } from '~src/connectivity/services/OAuthCredentialsService';
import { IDataConnectionInfo } from '~src/datasource/dataConnection/models/IDataConnectionInfo';
import { IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { getSessionManagementServiceInstance} from '~src/sessionManagement/SessionManagementService';

import {
  CredentialOption, EditableConnection,
  IOAuthConnectionEditModel,
  ITestConnectionResult, PasswordUnchanged,
  SalesforceConnectionType
} from '../EditConnection/IEditConnectionDialog';
import { EditOAuthConnectionDialogBody } from './components/OAuth/EditOAuthConnectionDialog';
import { EditSalesforceConnectionDialogBody } from './components/Salesforce/EditSalesforceConnectionDialog';
import { EditOAuthConnectionUtils } from './utils/EditOAuthConnectionUtils';

/** A sentinel credential id to mean "Add new OAuth credential" */
const ADD_NEW_OAUTH_CREDENTIAL_OPTION_ID = '__ADD_NEW_OAUTH_CREDENTIAL';

export type EditOAuthConnectionDialogProps = Readonly<{
    firstConnection: EditableConnection;
    prompt: string;
    promptEmbedCredentials: string;
    promptNoCredential: string;
    describeEmbedCredential: string;
    describeNoCredential: string;
    addNewOAuthCredentialOptionValue: string;
    currentConnectionTypeDisplayName: string;
    showNetworkType: boolean;
    isTestConnectionDisabled: (
        dialogState: EditOAuthConnectionDialogState,
    ) => boolean;
    testConnection: (
        dialogState: EditOAuthConnectionDialogState,
    ) => Promise<ITestConnectionResult>;
}>;

export type EditOAuthConnectionDialogState = Readonly<{
    credentialOption: CredentialOption;
    credentialId: string;
    credentialNoValueOptionText: string;
    credentials: IOAuthCredential[];
    username: string;
    password: string;
    isPasswordEmbedded: boolean;
    usingManagedKeychain?: boolean;
    usingRemoteQueryAgent?: boolean;
}>;
export namespace EditOAuthConnectionAction {

    /**
     * The code below assumes that all the connections are of the same type
     * @param connections
     */
    export async function confirmEditOAuthDatabaseConnections(
        connections: EditableConnection[]
    ): Promise<Promise<any>> {

        const { editConnectionDialogProps, editConnectionDialogState } = await createDialogPropsAndInitialState(connections);
        const modalInstance = openReactActionDialog<EditOAuthConnectionDialogProps, EditOAuthConnectionDialogState, IResult>({
            title: Messages.editConnection(),
            bodyComponent: connections[0].connectionType === SalesforceConnectionType ? EditSalesforceConnectionDialogBody : EditOAuthConnectionDialogBody,
            initialBodyProps: editConnectionDialogProps,
            initialDialogState: editConnectionDialogState,
            actionName: Messages.save(),
            action: (_props, state) => EditOAuthConnectionUtils.updateOAuthConnectionsToServer(
                connections,
                state.credentialOption,
                state.username,
                state.password,
                state.credentialId,
                state.credentialOption === 'password',
              state.usingRemoteQueryAgent
            ),
            dialogWidth: TabDialogWidth.width500px,
            dismissDialogOnActionFailed: true

        });
        return modalInstance.result;
    }

    async function createDialogPropsAndInitialState(connections: EditableConnection[]) {
        const firstConnectionType = connections[0].connectionType;
        const firstConnectionDetails = connections[0].connectionDetails;
        const firstConnection: ReadOnlyEditableConnection  = connections[0];

        const datasource: IDataSourceInfo = ((firstConnection as IDataConnectionInfo).datasource) as IDataSourceInfo;

      let instanceUrl: string;
        let role: string;
        if (firstConnectionDetails.type === 'database') {
            instanceUrl = firstConnectionDetails.instanceUrl;
            // If the snowflake connection has a default role (i.e. role=''), set the role as an empty string
            role = firstConnectionType === 'snowflake' ? (firstConnectionDetails.role ?? '') : firstConnectionDetails.role;
        }
        const oAuthCredentialTypeInfo: IOAuthCredentialTypeInfo = OAuthCredentialsService.getTypeInfo(firstConnectionType);
        const oauthModel: IOAuthConnectionEditModel = await createOAuthEditModel(connections);


        let credentialsQuery: ResourceQuery<IOAuthCredential> =
            OAuthCredentials.allValid().addFilter(Filter.fieldEq('type', firstConnectionType));

        if(instanceUrl)
            credentialsQuery = credentialsQuery.addFilter(Filter.fieldEq('instanceUrl', instanceUrl));
        if(role != null)
            credentialsQuery = credentialsQuery.addFilter(Filter.fieldEq('role', role));

        const result = await SliceableHelper.fetchAll(credentialsQuery);
        const credentials = result.items;

        const testConnection = (
            dialogState: EditOAuthConnectionDialogState,
        ): Promise<ITestConnectionResult> => {
            const usernamePassword = { username: dialogState.username, password: { isEmbedded: dialogState.isPasswordEmbedded, newPassword: dialogState.password } };
            return dialogState.credentialOption === 'password'
                ? EditOAuthConnectionUtils.checkUsernamePasswordConnection(connections[0].id, usernamePassword)
                : EditOAuthConnectionUtils.checkOAuthConnection(dialogState.credentialId);
        };


        const isTestConnectionDisabled = (
            dialogState: EditOAuthConnectionDialogState,
        ): boolean => {
            const showTestConnection = connections.length === 1
                && OAuthCredentialsService.supportsTestConnection(firstConnectionType)
                && connections[0].testingConnectionAllowed;
            if (!showTestConnection) {
                return true;
            }
            return dialogState.credentialOption === 'mixed'
                || (dialogState.credentialOption === 'password' && !dialogState.username)
                || (dialogState.credentialOption === 'oauth' && !dialogState.credentialId)
                || (dialogState.credentialOption === 'none')
                || (dialogState.usingRemoteQueryAgent === true);
        };

        const displayStrings = OAuthCredentialsService.getDisplayStrings(firstConnectionType);


        const editConnectionDialogState: EditOAuthConnectionDialogState = {
            credentialOption: oauthModel.credentialOption,
            credentialId: oauthModel.credentialId,
            credentialNoValueOptionText: oauthModel.credentialNoValueOptionText,
            credentials: credentials,
            username: oauthModel.usernamePassword.username,
            password: oauthModel.usernamePassword.password.newPassword,
            isPasswordEmbedded: oauthModel.usernamePassword.password.isEmbedded,
            usingManagedKeychain: oauthModel.usingManagedKeychain,
            usingRemoteQueryAgent: oauthModel.usingRemoteQueryAgent,
        };
        const editConnectionDialogProps: EditOAuthConnectionDialogProps = {
            firstConnection: connections[0],
            prompt: EditOAuthConnectionUtils.makePrompt(connections.length),
            promptEmbedCredentials: displayStrings.promptEmbedCredential,
            promptNoCredential: displayStrings.noCredential,
            describeEmbedCredential: displayStrings.describeEmbedCredential,
            describeNoCredential: displayStrings.describeNoCredential,
            addNewOAuthCredentialOptionValue: OAuthCredentials.canAddForConnectionType(oAuthCredentialTypeInfo) ? ADD_NEW_OAUTH_CREDENTIAL_OPTION_ID : undefined,
            currentConnectionTypeDisplayName: connections[0].connectionTypeDisplayName,
            showNetworkType: getSessionManagementServiceInstance().isOnlineEnvironment() &&
              EditConnectionReactActionUtils.showNetworkType(datasource),
            isTestConnectionDisabled: isTestConnectionDisabled,
            testConnection: testConnection,

        };

        return { editConnectionDialogProps, editConnectionDialogState };
    }

    async function createOAuthEditModel(
        connections: EditableConnection[],
    ): Promise<IOAuthConnectionEditModel> {
        const editModel: IOAuthConnectionEditModel = {
            credentialOption: 'mixed',
            credentialId: undefined,
            credentialNoValueOptionText: undefined,
            usingManagedKeychain: undefined,
            usingRemoteQueryAgent: undefined,
            usernamePassword: {
                username: '',
                password: { isEmbedded: false, newPassword: '' }
            }
        };

        if(connections.length > 0) {
          editModel.usingRemoteQueryAgent = connections[0].usingRemoteQueryAgent;
        }

        const result = await OAuthCredentials.commonCredentialForDatasources(connections.map(c => c.id)) as IGetCommonUserOAuthCredentialResult;
        let numAuthenticationTypes = 0;
        if (result.hasEmbeddedPassword) numAuthenticationTypes++;
        if (result.hasManagedKeychain) {
            numAuthenticationTypes++;
            editModel.usingManagedKeychain = true;
        }
        if (result.hasOAuthCredential) numAuthenticationTypes++;

        // If all connections have the same authentication type then select the proper option and fill in common details
        if (numAuthenticationTypes !== 1) {
            // TFSID:608975
            // Making NONE the default option to make clear that the data source doesn't have a valid set of credentials.
            editModel.credentialOption = 'none';
        } else {
            if (result.hasEmbeddedPassword) {
                editModel.credentialOption = 'password';
                editModel.usernamePassword = {
                    username: result.commonUsername,
                    password: { isEmbedded: true, newPassword: PasswordUnchanged }
                };
            } else if (result.hasManagedKeychain || result.hasOAuthCredential) {
                editModel.credentialOption = 'oauth';
                if (result.oauthCredentialId) {
                    editModel.credentialId = result.oauthCredentialId;
                } else {
                    // This user can see the credential but does not own it.  This displays it as an option with no value
                    editModel.credentialNoValueOptionText = result.commonUsername;
                }
            }
        }

        return editModel;
    }
}